import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import {
  OrganizationPlan,
  organizationPlanIconsMap,
  OrganizationPlanKey,
} from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import AddOns from 'dashboard/views/DashboardOrganizationBillingOverviewView/AddOns';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';

export interface CurrentPlanCardProps {
  currentOrganizationPlan: OrganizationPlan | null;
  pendingOrganizationPlanMigration: OrganizationPlanMigration | null;
}

const CurrentPlanCard = ({
  currentOrganizationPlan,
  pendingOrganizationPlanMigration,
}: CurrentPlanCardProps) => {
  return (
    <CardWrapper>
      <Card.Body>
        <Heading size='md'>
          {currentOrganizationPlan?.attributes['display-name'] || 'Pending'}
        </Heading>
        {currentOrganizationPlan ? (
          <>
            <RowWrapper>
              <DescriptionWrapper>
                <Spacer height={2} />
                <Paragraph size='md' color='primary'>
                  {currentOrganizationPlan.attributes.key in currentPlanDescriptionMap
                    ? currentPlanDescriptionMap[currentOrganizationPlan.attributes.key]
                    : 'Your business is currently on a custom plan with Persona.'}
                </Paragraph>
              </DescriptionWrapper>
              <IconWrapper>
                {organizationPlanIconsMap[currentOrganizationPlan.attributes['display-icon-key']]}
              </IconWrapper>
            </RowWrapper>
            {!!pendingOrganizationPlanMigration?.attributes?.['requested-plan-display-name'] && (
              <>
                <Spacer height={2} />
                <PendingPlanMigrationHeader>
                  We’re reviewing your request
                </PendingPlanMigrationHeader>
                <Paragraph size='md' color='primary'>
                  We’re reviewing your request to switch to the{' '}
                  {pendingOrganizationPlanMigration.attributes['requested-plan-display-name']} Plan,
                  and you’ll receive an email update in the next 3-5 business days.
                </Paragraph>
              </>
            )}
          </>
        ) : (
          <Paragraph size='md' color='primary'>
            We’re putting the final touches on your new Persona instance. Everything should be ready
            in a couple of minutes — try refreshing the page for updates.
          </Paragraph>
        )}
        {currentOrganizationPlan?.attributes.key === OrganizationPlanKey.Essential && (
          <>
            <Spacer height={2} />
            <AddOns />
          </>
        )}
      </Card.Body>
    </CardWrapper>
  );
};

export const currentPlanDescriptionMap: Partial<Record<OrganizationPlanKey, ReactNode>> = {
  [OrganizationPlanKey.Churned]: (
    <>
      Your Persona account is currently inactive. Choose a plan below or{' '}
      <Link to='/dashboard/contact-us'>contact us</Link> to reactivate.
    </>
  ),
  [OrganizationPlanKey.Essential]: (
    <>
      Your business is currently on the Essential Plan. Thanks for being a star customer. &#11088;
    </>
  ),
  [OrganizationPlanKey.Growth]: (
    <>Your business is currently on the Growth Plan. Thanks for being a star customer. &#11088;</>
  ),
  [OrganizationPlanKey.SelfServeSandbox]: (
    <>
      Your business is currently trialing Persona with mocked sample data in a Sandbox environment.
      Make it official by selecting a plan below! &#10024;
    </>
  ),
  [OrganizationPlanKey.Starter]: (
    <>
      Your business is currently part of the Persona Startup Program. Thanks for being a star
      customer. &#11088;
    </>
  ),
  [OrganizationPlanKey.StarterPenguin]: (
    <>
      Your business is currently part of the Persona Startup Program. Thanks for being a star
      customer. &#11088;
    </>
  ),
  [OrganizationPlanKey.TrialEssential]: (
    <>
      Your business is currently trialing Persona on the Essential plan. Enjoying the sneak peek? We
      love having you here. Make it official by selecting a plan below. &#10024;
    </>
  ),
  [OrganizationPlanKey.TrialGrowth]: (
    <>
      Your business is currently trialing Persona on the Growth plan. Enjoying the sneak peek? We
      love having you here. Make it official by selecting a plan below. &#10024;
    </>
  ),
  [OrganizationPlanKey.TrialSalesProspect]: (
    <>
      Your business is currently trialing Persona. Enjoying the sneak peek? We love having you here.
      Make it official by selecting a plan below. &#10024;
    </>
  ),
};

const CardWrapper = styled(Card)`
  background-color: ${Color.CorePurple100};
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

const DescriptionWrapper = styled.div`
  max-width: 610px;
`;

const IconWrapper = styled.div`
  position: absolute;
  bottom: -75px;
  right: 0px;
  padding: ${Spacing[4]};

  svg {
    width: 150px;
    height: 150px;
  }

  @media (max-width: 1150px) {
    display: none;
  }
`;

const PendingPlanMigrationHeader = styled.div`
  ${Typography.TextLargeBold};
`;

export default CurrentPlanCard;
